import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import {
  ScheduleService,
  type AgentSchedule,
  type AgentScheduleRunner,
  type AgentScheduleRun,
  type ScheduleConversation,
} from '../services/scheduleService';
import { ShareService } from '../services/shareService';

// --- State ---

export interface AgentScheduleState {
  schedules: AgentSchedule[];
  scheduleRuns: Record<string, AgentScheduleRun[]>; // keyed by scheduleId
  runnerConversations: Record<string, ScheduleConversation[]>; // keyed by runnerId
  isLoading: boolean;
  error: string | null;
}

const initialState: AgentScheduleState = {
  schedules: [],
  scheduleRuns: {},
  runnerConversations: {},
  isLoading: false,
  error: null,
};

// --- Async Thunks ---

export const fetchSchedules = createAsyncThunk(
  'agentSchedule/fetchSchedules',
  async () => {
    return await ScheduleService.listSchedules();
  }
);

export const createSchedule = createAsyncThunk(
  'agentSchedule/createSchedule',
  async (input: {
    id?: string;
    title: string;
    description?: string;
    runtime: string;
    cronExpression: string;
    timezone: string;
    nextRunAt: string;
    systemPrompt?: string;
    projectId?: string;
    metadata?: string;
    runners: { id?: string; title: string; prompt: string; attachments?: string; sendEmail?: boolean; emailCondition?: string | null; emailConditionMode?: string | null }[];
  }, { rejectWithValue }) => {
    const { runners, ...scheduleInput } = input;

    let schedule: AgentSchedule;
    try {
      schedule = await ScheduleService.createSchedule(scheduleInput);
    } catch (err: any) {
      console.error('[createSchedule] mutation failed:', JSON.stringify(err), err?.errors, err?.message);
      return rejectWithValue(err?.errors?.[0]?.message ?? 'Failed to create schedule');
    }

    const createdRunners: AgentScheduleRunner[] = [];
    try {
      for (let i = 0; i < runners.length; i++) {
        const runner = await ScheduleService.createRunner({
          id: runners[i].id,
          scheduleId: schedule.id,
          title: runners[i].title,
          prompt: runners[i].prompt,
          attachments: runners[i].attachments,
          sortOrder: i,
          sendEmail: runners[i].sendEmail,
          emailCondition: runners[i].emailCondition,
          emailConditionMode: runners[i].emailConditionMode,
        });
        createdRunners.push(runner);
      }
    } catch (err) {
      // Roll back: delete the schedule (and any runners already created) so we
      // don't leave a partial schedule that the poller would fire on.
      try {
        await Promise.all(createdRunners.map((r) => ScheduleService.deleteRunner(r.id)));
      } catch (cleanupErr) {
        console.error('[createSchedule] Runner cleanup failed:', cleanupErr);
      }
      try {
        await ScheduleService.deleteSchedule(schedule.id);
      } catch (cleanupErr) {
        console.error('[createSchedule] Schedule cleanup failed:', cleanupErr);
      }
      return rejectWithValue(err instanceof Error ? err.message : 'Failed to create runners');
    }

    return {
      ...schedule,
      runners: { items: createdRunners },
    } as AgentSchedule;
  }
);

export const updateSchedule = createAsyncThunk(
  'agentSchedule/updateSchedule',
  async (input: {
    id: string;
    title?: string;
    description?: string;
    runtime?: string;
    cronExpression?: string;
    timezone?: string;
    status?: 'ACTIVE' | 'PAUSED' | 'DELETED';
    nextRunAt?: string;
    systemPrompt?: string;
    error?: string | null; // e.g. cleared on Resume after an auto-pause
  }) => {
    return await ScheduleService.updateSchedule(input);
  }
);

export const deleteSchedule = createAsyncThunk(
  'agentSchedule/deleteSchedule',
  async (
    { id, deactivateShares }: { id: string; deactivateShares?: boolean },
    { getState }
  ) => {
    // Cascade-deactivate the runners' live share links first (user opted in via the delete
    // modal's checkbox). Best-effort: a share we can't flip never blocks the delete.
    if (deactivateShares) {
      const { schedules } = (getState() as { agentSchedule: AgentScheduleState }).agentSchedule;
      const runnerIds = (schedules.find((s) => s.id === id)?.runners?.items || []).map((r) => r.id);
      if (runnerIds.length) {
        const shareIds = await ShareService.listActiveShareIdsForRunners(runnerIds);
        if (shareIds.length) await ShareService.deactivateShares(shareIds);
      }
    }
    await ScheduleService.deleteSchedule(id);
    return id;
  }
);

export const addRunner = createAsyncThunk(
  'agentSchedule/addRunner',
  async (input: {
    id?: string;
    scheduleId: string;
    title: string;
    prompt: string;
    attachments?: string;
    sortOrder?: number;
    sendEmail?: boolean;
    emailCondition?: string | null;
    emailConditionMode?: string | null;
  }) => {
    return await ScheduleService.createRunner(input);
  }
);

export const updateRunner = createAsyncThunk(
  'agentSchedule/updateRunner',
  async (input: {
    id: string;
    title?: string;
    prompt?: string;
    attachments?: string;
    sortOrder?: number;
    sendEmail?: boolean;
    emailCondition?: string | null;
    emailConditionMode?: string | null;
  }) => {
    return await ScheduleService.updateRunner(input);
  }
);

export const removeRunner = createAsyncThunk(
  'agentSchedule/removeRunner',
  async ({ runnerId, scheduleId, deactivateShares }: { runnerId: string; scheduleId: string; deactivateShares?: boolean }) => {
    // Cascade-deactivate this runner's live share links first (opt-in, best-effort).
    if (deactivateShares) {
      const shareIds = await ShareService.listActiveShareIdsForRunner(runnerId);
      if (shareIds.length) await ShareService.deactivateShares(shareIds);
    }
    await ScheduleService.deleteRunner(runnerId);
    return { runnerId, scheduleId };
  }
);

export const fetchRunnerConversations = createAsyncThunk(
  'agentSchedule/fetchRunnerConversations',
  async ({ runnerId, limit }: { runnerId: string; limit?: number }) => {
    const conversations = await ScheduleService.getRunnerConversations(runnerId, limit);
    return { runnerId, conversations };
  }
);

export const fetchScheduleRuns = createAsyncThunk(
  'agentSchedule/fetchScheduleRuns',
  async ({ scheduleId, limit }: { scheduleId: string; limit?: number }) => {
    const runs = await ScheduleService.getScheduleRuns(scheduleId, limit);
    return { scheduleId, runs };
  }
);

// --- Slice ---

const agentScheduleSlice = createSlice({
  name: 'agentSchedule',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    // fetchSchedules
    builder.addCase(fetchSchedules.pending, (state) => {
      state.isLoading = true;
      state.error = null;
    });
    builder.addCase(fetchSchedules.fulfilled, (state, action) => {
      state.isLoading = false;
      state.schedules = action.payload;
    });
    builder.addCase(fetchSchedules.rejected, (state, action) => {
      state.isLoading = false;
      state.error = action.error.message || 'Failed to fetch schedules';
    });

    // createSchedule
    builder.addCase(createSchedule.fulfilled, (state, action) => {
      state.schedules.unshift(action.payload);
    });

    // updateSchedule
    builder.addCase(updateSchedule.fulfilled, (state, action) => {
      const idx = state.schedules.findIndex((s) => s.id === action.payload.id);
      if (idx !== -1) {
        state.schedules[idx] = { ...state.schedules[idx], ...action.payload };
      }
    });

    // deleteSchedule
    builder.addCase(deleteSchedule.fulfilled, (state, action) => {
      state.schedules = state.schedules.filter((s) => s.id !== action.payload);
    });

    // addRunner
    builder.addCase(addRunner.fulfilled, (state, action) => {
      const schedule = state.schedules.find((s) => s.id === action.payload.scheduleId);
      if (schedule) {
        if (!schedule.runners) {
          schedule.runners = { items: [] };
        }
        schedule.runners.items.push(action.payload);
      }
    });

    // updateRunner
    builder.addCase(updateRunner.fulfilled, (state, action) => {
      for (const schedule of state.schedules) {
        const runners = schedule.runners?.items || [];
        const idx = runners.findIndex((r) => r.id === action.payload.id);
        if (idx !== -1) {
          runners[idx] = { ...runners[idx], ...action.payload };
          break;
        }
      }
    });

    // removeRunner
    builder.addCase(removeRunner.fulfilled, (state, action) => {
      const schedule = state.schedules.find((s) => s.id === action.payload.scheduleId);
      if (schedule?.runners) {
        schedule.runners.items = schedule.runners.items.filter(
          (r) => r.id !== action.payload.runnerId
        );
      }
    });

    // fetchRunnerConversations
    builder.addCase(fetchRunnerConversations.fulfilled, (state, action) => {
      state.runnerConversations[action.payload.runnerId] = action.payload.conversations;
    });

    // fetchScheduleRuns
    builder.addCase(fetchScheduleRuns.fulfilled, (state, action) => {
      state.scheduleRuns[action.payload.scheduleId] = action.payload.runs;
    });
  },
});

export const { clearError } = agentScheduleSlice.actions;
export default agentScheduleSlice.reducer;
